/**
 * Welcome.js  — Diet Planner, Screen 1
 *
 * Introduces the AI Diet Planner and starts an anonymous session. Collects
 * no data on this screen.
 *
 * Mirrors the Yog.js module's structure and conventions:
 *  ✓ WelcomePageMeta   — title/description/OG/canonical/JSON-LD
 *  ✓ useWelcomeAnalytics — 'assessment_start' + supporting lifecycle events
 *  ✓ Styled-components imported from ./Welcome.styles, themed via DIET_CSS_VARS
 *
 * Functional rules implemented:
 *  1. Trust markers (No Login Required, 100% Free, ~3 Minutes) must render
 *     before the CTA button becomes clickable.
 *  2. Back-button/refresh before starting must not create duplicate sessions
 *     — session_id is created lazily and persisted in sessionStorage, and
 *     the 'assessment_start' event is only fired once per session.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useWelcomeAnalytics from "./useWelcomeAnalytics";
import { WelcomePageMeta } from "./WelcomeSeoMeta";
import {
  Section,
  AppShell,
  Header,
  Brand,
  HeaderTag,
  Badge,
  Hero,
  HeroGrid,
  HeroLeft,
  HeroTitle,
  TitleAccent,
  HeroSubhead,
  CtaButton,
  TrustMarkers,
  TrustItem,
  TrustIcon,
  TrustText,
  TrustTitle,
  TrustSub,
  Features,
  FeatureCard,
  FeatureHead,
  FeatureIcon,
  FeatureHeadText,
  FeatureTitle,
  FeatureSubtitle,
  FeatureDesc,
  FeatureFooter,
  FeatureArrow,
  SessionNote,
} from "./Welcome.styles";

// ─── Session storage keys ───────────────────────────────────────────────────────
const SESSION_ID_KEY      = "ys_diet_session_id";
const SESSION_STARTED_KEY = "ys_diet_session_started";

// ─── Session helpers ─────────────────────────────────────────────────────────────
// A session_id is only ever created once per browser session and reused on
// refresh/back-nav — never regenerated, so no duplicate sessions are created.
const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return null;
  let sessionId = window.sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId =
      "sess_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
    window.sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

const hasSessionStarted = () =>
  typeof window !== "undefined" &&
  window.sessionStorage.getItem(SESSION_STARTED_KEY) === "true";

// ─── Default forward destination ────────────────────────────────────────────────
// Matches the existing "/dietPlanner" route in App.js. If your assessment flow
// ever moves to its own route, update this constant (or just pass
// onNavigateNext to override it entirely).
const DEFAULT_NEXT_PATH = "/dietPlanner";

// ═══════════════════════════════════════════════════════════════════════════════
// ICONS — small inline SVGs, no external icon-library dependency.
// All accept normal <svg> props (width/height/className/etc.) and inherit
// color via `currentColor` so they pick up the accent color of their wrapper.
// ═══════════════════════════════════════════════════════════════════════════════

const iconBase = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
};

const IconSparkle = (props) => (
  <svg {...iconBase} width="14" height="14" {...props}>
    <path d="M12 3l1.6 4.9L18 9.5l-4.4 1.6L12 16l-1.6-4.9L6 9.5l4.4-1.6L12 3z" />
  </svg>
);

const IconClock = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3.2 1.8" />
  </svg>
);

const IconShieldCheck = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <path d="M12 3.2l6.5 2.6v5.4c0 4.4-2.8 7.6-6.5 8.6-3.7-1-6.5-4.2-6.5-8.6V5.8L12 3.2z" />
    <path d="M9 12l2 2 4-4.2" />
  </svg>
);

const IconUser = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <circle cx="12" cy="8.2" r="3.6" />
    <path d="M4.5 19.5c0-4 3.4-6 7.5-6s7.5 2 7.5 6" />
  </svg>
);

const IconLeaf = (props) => (
  <svg {...iconBase} width="17" height="17" {...props}>
    <path d="M5 20c0-8.5 5-15 14-15 0 9.5-6.5 15-14 15z" />
    <path d="M5.5 19.5C8 16 11 12.5 14.5 8.5" />
  </svg>
);

const IconArrowRight = (props) => (
  <svg {...iconBase} width="16" height="16" {...props}>
    <path d="M4 12h15.2" />
    <path d="M13.5 5.5L20 12l-6.5 6.5" />
  </svg>
);

const IconClipboardHeart = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <rect x="5.5" y="4.2" width="13" height="16.6" rx="2.2" />
    <path d="M9 4.2h6a1 1 0 011 1V7H8V5.2a1 1 0 011-1z" />
    <path d="M12 15.4c-2.4-1.6-3.6-2.8-3.6-4.3a2.1 2.1 0 013.6-1.5 2.1 2.1 0 013.6 1.5c0 1.5-1.2 2.7-3.6 4.3z" />
  </svg>
);

const IconSprout = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <path d="M12 13V21" />
    <path d="M12 13c0-3.3-2.5-5.6-6-5.9C6.3 10.7 8.7 13 12 13z" />
    <path d="M12 10.4c0-3.3 2.5-5.6 6-5.9-.3 3.6-2.7 5.9-6 5.9z" />
  </svg>
);

const IconLotus = (props) => (
  <svg {...iconBase} width="20" height="20" {...props}>
    <circle cx="12" cy="6.4" r="2.1" />
    <path d="M12 8.7v3.6" />
    <path d="M12 12.3c-2.8.8-4.8 2.6-5.6 4.6" />
    <path d="M12 12.3c2.8.8 4.8 2.6 5.6 4.6" />
    <path d="M9.3 15.4c-1.7.7-2.7.9-2.7.9" />
    <path d="M14.7 15.4c1.7.7 2.7.9 2.7.9" />
  </svg>
);

// ─── Trust marker content ───────────────────────────────────────────────────────
const TRUST_ITEMS = [
  { icon: IconClock,       title: "3 Minutes", sub: "Quick & Easy" },
  { icon: IconShieldCheck, title: "100% Free",  sub: "No Hidden Charges" },
  { icon: IconUser,        title: "No Login",   sub: "Get Started Instantly" },
];

// ─── Feature card content ───────────────────────────────────────────────────────
const FEATURES = [
  {
    key: "diet",
    icon: IconClipboardHeart,
    title: "Diet",
    subtitle: "AI Health Analysis",
    desc: "Advanced AI analyzes your body, lifestyle & goals to create a plan that's right for you.",
  },
  {
    key: "meals",
    icon: IconSprout,
    title: "Meals",
    subtitle: "Indian-First Meal Plans",
    desc: "Delicious, home-style Indian meals designed around your preferences and needs.",
  },
  {
    key: "yoga",
    icon: IconLotus,
    title: "Yoga",
    subtitle: "Yoga-Aware Nutrition",
    desc: "Plans that work in harmony with your yoga practice for holistic well-being.",
  },
];

// Accent CSS vars per feature — set inline so Welcome.styles.js stays free of
// per-card conditionals; --accent-fg/--accent-bg are consumed by FeatureIcon,
// FeatureTitle and FeatureArrow.
const ACCENT_STYLE = {
  diet:  { "--accent-fg": "var(--c-diet-fg)",  "--accent-bg": "var(--c-diet-bg)" },
  meals: { "--accent-fg": "var(--c-meals-fg)", "--accent-bg": "var(--c-meals-bg)" },
  yoga:  { "--accent-fg": "var(--c-yoga-fg)",  "--accent-bg": "var(--c-yoga-bg)" },
};

/**
 * Welcome
 *
 * @param {object}   props
 * @param {function} [props.onNavigateNext] - Called once the session has
 *   started, so a parent flow (e.g. <DietPlannerWizard>) can render its own
 *   next step instead of a full page navigation. Receives the session_id.
 *   If omitted (e.g. when Welcome is mounted directly on its own route, as
 *   it is at "/welcome" in App.js), Welcome navigates itself to
 *   DEFAULT_NEXT_PATH via react-router.
 */
const Welcome = ({ onNavigateNext }) => {
  const navigate = useNavigate();
  const analytics = useWelcomeAnalytics();

  const [sessionId] = useState(getOrCreateSessionId);
  const [sessionStarted, setSessionStarted] = useState(hasSessionStarted);
  const [trustMarkersVisible, setTrustMarkersVisible] = useState(false);
  const trustMarkersFiredRef = useRef(false);

  // Log the page view once on mount — loading/refreshing this screen never
  // creates a session record; only clicking Start does (see handleStart).
  useEffect(() => {
    analytics.trackPageView(sessionId);
  }, [analytics, sessionId]);

  // Reveal trust markers on mount. The CTA button stays disabled until this
  // has visibly happened, satisfying the "trust markers render before the
  // button is clickable" rule.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setTrustMarkersVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleTrustMarkersTransitionEnd = useCallback(() => {
    if (trustMarkersFiredRef.current) return;
    trustMarkersFiredRef.current = true;
    analytics.trackTrustMarkersRendered(sessionId);
  }, [analytics, sessionId]);

  const handleStart = useCallback(() => {
    if (!sessionStarted) {
      window.sessionStorage.setItem(SESSION_STARTED_KEY, "true");
      setSessionStarted(true);
      analytics.trackAssessmentStart(sessionId);
    }

    if (typeof onNavigateNext === "function") {
      // Parent flow (e.g. DietPlannerWizard) owns navigation — let it decide.
      onNavigateNext(sessionId);
    } else {
      // Standalone route (e.g. "/welcome" in App.js) — move forward ourselves.
      navigate(DEFAULT_NEXT_PATH, { state: { sessionId } });
    }
  }, [sessionStarted, sessionId, analytics, onNavigateNext, navigate]);

  return (
    <Section>
      <WelcomePageMeta />

      <AppShell>
        {/* <Header>
          <Brand>YogaShastra</Brand>
          <HeaderTag>AI Diet Planner</HeaderTag>
        </Header> */}

        <Badge>
          <IconSparkle />
          AI-Powered &bull; Yoga-Inspired
        </Badge>

        <Hero>
          <HeroGrid>
            <HeroLeft>
              <HeroTitle>Personalized Nutrition, Powered by AI</HeroTitle>
              <TitleAccent aria-hidden="true">
                <span />
                <em />
              </TitleAccent>
              <HeroSubhead>
                Get a scientifically balanced diet plan in under 3 minutes —
                no sign-up needed.
              </HeroSubhead>

              <CtaButton
                type="button"
                disabled={!trustMarkersVisible}
                onClick={handleStart}
                aria-label="Start free assessment"
              >
                <IconLeaf />
                Start Free Assessment
                <IconArrowRight className="arrow" />
              </CtaButton>
            </HeroLeft>

            <TrustMarkers
              $visible={trustMarkersVisible}
              onTransitionEnd={handleTrustMarkersTransitionEnd}
            >
              {TRUST_ITEMS.map(({ icon: Icon, title, sub }) => (
                <TrustItem key={title}>
                  <TrustIcon>
                    <Icon />
                  </TrustIcon>
                  <TrustText>
                    <TrustTitle>{title}</TrustTitle>
                    <TrustSub>{sub}</TrustSub>
                  </TrustText>
                </TrustItem>
              ))}
            </TrustMarkers>
          </HeroGrid>
        </Hero>

        <Features>
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <FeatureCard key={f.key} $index={i} style={ACCENT_STYLE[f.key]}>
                <FeatureHead>
                  <FeatureIcon>
                    <Icon />
                  </FeatureIcon>
                  <FeatureHeadText>
                    <FeatureTitle>{f.title}</FeatureTitle>
                    <FeatureSubtitle>{f.subtitle}</FeatureSubtitle>
                  </FeatureHeadText>
                </FeatureHead>

                <FeatureDesc>{f.desc}</FeatureDesc>

                <FeatureFooter>
                  <FeatureArrow>
                    <IconArrowRight />
                  </FeatureArrow>
                </FeatureFooter>
              </FeatureCard>
            );
          })}
        </Features>

        {sessionStarted && (
          // Momentary — navigation (either onNavigateNext or the default
          // route push below) fires in the same click, so this is mostly
          // useful if navigation is ever delayed (e.g. an async onNavigateNext).
          <SessionNote>Session {sessionId} — starting your assessment…</SessionNote>
        )}
      </AppShell>
    </Section>
  );
};

Welcome.displayName = "Welcome";
export default React.memo(Welcome);